import { gql } from "@apollo/client";
import { Checkbox } from "@mui/material";
import Stack from "atoms/Stack";
import { MOBILE_LAYOUT_BREAKPOINT } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_DENSE } from "helpers/spacings";
import useData from "helpers/useData";
import Section, { SectionLabel } from "molecules/Section";
import {
  CourtMarkings,
  LINE,
  MAKE_COLOR,
  MAKE_STROKE,
  MARKER_R,
  MISS_COLOR,
  MISS_STROKE,
  SHOT_OPACITY,
  ShotMarker,
  SVG_H,
  SVG_W,
  toSvgX,
  toSvgY,
} from "pages/games/BasketballCourt";
import HighlightPlayerDialog from "pages/video_player/HighlightPlayerDialog";
import React, { useMemo, useRef, useState } from "react";
import { useWindowSize } from "react-use-size";

const NORMAL_PERIOD_COUNTS = { quarters: 4, halves: 2, thirds: 3, single: 1 };

// Scorekeeper V3 incorrectly mirrors coordinates for the second half of regulation, so we
// flip those back. This only applies to Scorekeeper-imported events — shots a user added
// or edited in the highlight editor are already stored in true (un-mirrored) coordinates.
function applyCoordinateCorrection(coords, periodNumber, normalPeriodCount) {
  if (periodNumber > normalPeriodCount / 2 && periodNumber <= normalPeriodCount) {
    return { x: coords.x, y: Math.abs(coords.y - 100) };
  }
  return coords;
}

function downloadSvgAsPng(svgEl, filename) {
  const scale = 2;
  const w = SVG_W * scale;
  const h = SVG_H * scale;
  const clone = svgEl.cloneNode(true);
  clone.setAttribute("width", w);
  clone.setAttribute("height", h);
  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const img = new Image();
  img.onload = () => {
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.src = url;
}

function ShotChartSvg({ events, svgRef, onClickEvent }) {
  return (
    <svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <CourtMarkings />

      {/* Shot markers:
          Note: we rotate the shot chart by 90 degrees so the baseline/hoop is displayed on top to bottom orientation.
          Scorekeeper has left to right orientation. It does impact the positioning.
      */}
      {events.map((event) => {
        const cx = toSvgX(event.coordinates.y);
        const cy = toSvgY(event.coordinates.x);
        const HIT_R = MARKER_R * 2;
        return (
          <g key={event.id} opacity={SHOT_OPACITY} style={{ cursor: "pointer" }} onClick={() => onClickEvent(event.id)}>
            <ShotMarker cx={cx} cy={cy} made={event.made} />
            {/* enlarged transparent hit area */}
            <circle cx={cx} cy={cy} r={HIT_R} fill="transparent" stroke="none" />
          </g>
        );
      })}

      {/* Legend bottom-right — fontSize 13 */}
      <g transform={`translate(${SVG_W - 108}, ${SVG_H - 16})`}>
        <circle cx={0} cy={6} r={5} fill="none" stroke={MAKE_COLOR} strokeWidth={MAKE_STROKE} />
        <text x={10} y={11} fontSize={13} fill={LINE} fontFamily={FONT_FAMILY_GRAPHIK}>
          Make
        </text>
        <line x1={49} y1={1} x2={59} y2={11} stroke={MISS_COLOR} strokeWidth={MISS_STROKE} strokeLinecap="round" />
        <line x1={49} y1={11} x2={59} y2={1} stroke={MISS_COLOR} strokeWidth={MISS_STROKE} strokeLinecap="round" />
        <text x={65} y={11} fontSize={13} fill={LINE} fontFamily={FONT_FAMILY_GRAPHIK}>
          Miss
        </text>
      </g>
    </svg>
  );
}

function PeriodFilterRow({ periods, periodFilter, setPeriodFilter }) {
  return (
    <Stack horizontal dense alignItemsCenter style={{ flexWrap: "wrap" }}>
      <SectionLabel label="All Periods" selected={periodFilter === "all"} onClick={() => setPeriodFilter("all")} />
      {periods.map((p) => (
        <SectionLabel key={p} label={p} selected={periodFilter === p} onClick={() => setPeriodFilter(p)} />
      ))}
    </Stack>
  );
}

function TeamShotChartSection({ participation, periods, periodFilter, setPeriodFilter, normalPeriodCount }) {
  const gamePlayers = participation.gamePlayers || [];

  const [selectedIds, setSelectedIds] = useState(() => gamePlayers.map((p) => p.id));
  const [showMakes, setShowMakes] = useState(true);
  const [showMisses, setShowMisses] = useState(true);
  const [dialogEventId, setDialogEventId] = useState(null);
  const svgRef = useRef(null);
  const windowSize = useWindowSize();
  const mobileLayout = windowSize.width < MOBILE_LAYOUT_BREAKPOINT;

  const allEvents = useMemo(
    () =>
      gamePlayers.flatMap((gp) =>
        (gp.events || []).map((e) => {
          // Old Scorekeeper (V3) imported events (no createdByPerson) need their 2nd-half coordinates
          // corrected; user-added/edited shots are already stored in true coordinates.
          // TODO: remove this once all events are stored in true coordinates in Scorekeeper V4+
          const isScorekeeperImported = !e.createdByPerson;
          const coords =
            isScorekeeperImported && e.coordinates
              ? applyCoordinateCorrection(e.coordinates, e.period?.number, normalPeriodCount)
              : e.coordinates;
          return { ...e, coordinates: coords, gamePlayerId: gp.id };
        }),
      ),
    [gamePlayers, normalPeriodCount],
  );

  // Base events: player + period filter — used for FG% (unaffected by shot type toggle)
  const baseEvents = useMemo(() => {
    let filtered = allEvents.filter((e) => selectedIds.includes(e.gamePlayerId));
    if (periodFilter !== "all") filtered = filtered.filter((e) => e.period?.name === periodFilter);
    return filtered;
  }, [allEvents, selectedIds, periodFilter]);

  const events = useMemo(() => {
    if (showMakes && showMisses) return baseEvents;
    if (!showMakes && !showMisses) return [];
    if (showMakes) return baseEvents.filter((e) => e.made === true);
    return baseEvents.filter((e) => e.made === false);
  }, [baseEvents, showMakes, showMisses]);

  const allSelected = selectedIds.length === gamePlayers.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const togglePlayer = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAll = () => setSelectedIds(allSelected ? [] : gamePlayers.map((p) => p.id));

  const handleDownload = () => {
    if (svgRef.current) {
      const teamName = participation.team?.name || "team";
      downloadSvgAsPng(svgRef.current, `shotchart-${teamName.toLowerCase().replace(/\s+/g, "-")}.png`);
    }
  };

  /* Download button style */
  const pillBtn = (active) => ({
    background: active ? COLOR_BLACK : "transparent",
    color: active ? COLOR_WHITE : LINE,
    borderRadius: 20,
    cursor: "pointer",
    fontSize: 11,
    fontFamily: FONT_FAMILY_GRAPHIK,
    fontWeight: active ? 500 : 400,
    padding: "6px 14px",
    lineHeight: 1.4,
  });

  return (
    <div style={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: SPACING }}>
      <HighlightPlayerDialog open={!!dialogEventId} eventId={dialogEventId} onClose={() => setDialogEventId(null)} />
      <div
        style={{
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontWeight: 500,
          fontSize: 14,
          letterSpacing: 0.4,
          color: COLOR_BLACK,
          borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
          paddingBottom: SPACING_DENSE,
        }}
      >
        {participation.team?.name}
      </div>
      {/* On mobile the period filter repeats under each team name (desktop keeps the single row up top) */}
      {mobileLayout && (
        <PeriodFilterRow periods={periods} periodFilter={periodFilter} setPeriodFilter={setPeriodFilter} />
      )}
      <div
        style={{
          display: "flex",
          flexDirection: mobileLayout ? "column" : "row",
          gap: SPACING,
          alignItems: mobileLayout ? "stretch" : "flex-start",
        }}
      >
        <div style={{ order: mobileLayout ? 2 : 1, width: mobileLayout ? "100%" : 190, flexShrink: 0 }}>
          <div
            onClick={toggleAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: SPACING_DENSE,
              cursor: "pointer",
              userSelect: "none",
              marginBottom: SPACING_DENSE,
            }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              size="small"
              sx={{
                padding: 0,
                color: COLOR_GREY_BORDER,
                "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#F1394B" },
              }}
            />
            <span style={{ fontFamily: FONT_FAMILY_GRAPHIK, fontSize: 13, fontWeight: 500, color: COLOR_BLACK }}>
              All Players
            </span>
          </div>
          <div
            style={
              mobileLayout
                ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: SPACING_DENSE }
                : { display: "flex", flexDirection: "column", gap: SPACING_DENSE, maxHeight: 400, overflowY: "auto" }
            }
          >
            {gamePlayers.map((gp) => {
              const checked = selectedIds.includes(gp.id);
              const label = `${gp.person?.fullNameWithAnonymisation}${gp.playerNumber ? ` - #${gp.playerNumber}` : ""}`;
              return (
                <div
                  key={gp.id}
                  onClick={() => togglePlayer(gp.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: SPACING_DENSE,
                    cursor: "pointer",
                    userSelect: "none",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    size="small"
                    sx={{
                      padding: 0,
                      color: COLOR_GREY_BORDER,
                      "&.Mui-checked": { color: "#F1394B" },
                    }}
                  />
                  <span
                    style={{
                      fontFamily: FONT_FAMILY_GRAPHIK,
                      fontSize: 13,
                      color: COLOR_BLACK,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div
          style={{
            order: mobileLayout ? 1 : 2,
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: SPACING_DENSE,
          }}
        >
          {/* Makes / Misses toggles + Download button (top-right, above the chart) */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACING_DENSE }}>
            <Stack horizontal dense alignItemsCenter>
              <SectionLabel label="Makes" selected={showMakes} onClick={() => setShowMakes((v) => !v)} />
              <SectionLabel label="Misses" selected={showMisses} onClick={() => setShowMisses((v) => !v)} />
            </Stack>
            <button style={pillBtn(true)} onClick={handleDownload}>
              Download
            </button>
          </div>

          {/* Court SVG — centred on mobile via block + margin auto */}
          <div style={{ border: `1px solid ${COLOR_GREY_BORDER}`, display: "block" }}>
            <ShotChartSvg events={events} svgRef={svgRef} onClickEvent={setDialogEventId} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShotChart({ gameId }) {
  const [periodFilter, setPeriodFilter] = useState("all");
  const windowSize = useWindowSize();
  const mobileLayout = windowSize.width < MOBILE_LAYOUT_BREAKPOINT;

  const [data] = useData(
    gql`
      query ShotChart($gameId: ID!) {
        game(id: $gameId) {
          id
          periodType
          participations {
            id
            isHomeTeam
            team {
              id
              name
            }
            gamePlayers {
              id
              playerNumber
              person {
                id
                fullNameWithAnonymisation
              }
              events(hasCoordinates: true) {
                id
                made
                createdByPerson {
                  id
                }
                period {
                  name
                  number
                }
                coordinates {
                  x
                  y
                }
              }
            }
          }
        }
      }
    `,
    { gameId },
    { skip: !gameId },
  );

  const game = data?.game;
  const participations = game?.participations || [];
  const normalPeriodCount = NORMAL_PERIOD_COUNTS[game?.periodType] ?? 4;

  const homeParticipation = participations.find((p) => p.isHomeTeam) || participations[0];
  const awayParticipation = participations.find((p) => !p.isHomeTeam) || participations[1];

  const allEvents = useMemo(
    () => participations.flatMap((p) => (p.gamePlayers || []).flatMap((gp) => gp.events || [])),
    [participations],
  );

  const periods = useMemo(() => {
    const names = new Set(allEvents.map((e) => e.period?.name).filter(Boolean));
    return [...names].sort();
  }, [allEvents]);

  if (!game || allEvents.length === 0) return null;

  return (
    <Section title="SHOT CHART">
      {/* Period filter — single row on desktop; on mobile it repeats under each team name instead */}
      {!mobileLayout && (
        <PeriodFilterRow periods={periods} periodFilter={periodFilter} setPeriodFilter={setPeriodFilter} />
      )}

      {/* Home + Away sections */}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: SPACING, marginTop: SPACING_DENSE, alignItems: "flex-start" }}
      >
        {homeParticipation && (
          <TeamShotChartSection
            key={homeParticipation.id}
            participation={homeParticipation}
            periods={periods}
            periodFilter={periodFilter}
            setPeriodFilter={setPeriodFilter}
            normalPeriodCount={normalPeriodCount}
          />
        )}
        {awayParticipation && awayParticipation !== homeParticipation && (
          <TeamShotChartSection
            key={awayParticipation.id}
            participation={awayParticipation}
            periods={periods}
            periodFilter={periodFilter}
            setPeriodFilter={setPeriodFilter}
            normalPeriodCount={normalPeriodCount}
          />
        )}
      </div>
    </Section>
  );
}
